import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { RefreshCw, UserCircle2, LogOut, Phone, Mail, ShieldCheck, Hash, Building2, Briefcase, Landmark, IdCard, Banknote, CreditCard, Copy, ChevronDown, ChevronUp, AlertTriangle } from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import { toast } from "sonner";

function InfoLine({ phone, email, pec, sdi }) {
  if (!phone && !email && !pec && !sdi) return null;
  // Office spec (20/02/2026): tutti i recapiti dell'header devono
  // essere in grassetto e con contrasto pieno, così sono leggibili
  // anche in stampa o screenshot di handover. Manteniamo il
  // colore neutro (slate-700) per non rubare protagonismo ai badge
  // colorati di Studio/Agenzia subito sopra.
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 text-xs font-semibold text-slate-700">
      {phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{phone}</span>}
      {email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{email}</span>}
      {pec && <span className="flex items-center gap-1"><ShieldCheck className="h-3 w-3" />PEC: {pec}</span>}
      {sdi && <span className="flex items-center gap-1"><Hash className="h-3 w-3" />SDI: {sdi}</span>}
    </div>
  );
}

export function AppHeader({
  currentOperator,
  isSaving,
  loadAllData,
  onLogout,
  settings = {}
}) {
  const studioHasData = settings.phoneStudio || settings.emailStudio || settings.pecStudio || settings.sdiCodeStudio;
  const agenziaHasData = settings.phoneAgenzia || settings.emailAgenzia || settings.pecAgenzia || settings.sdiCodeAgenzia;
  const reaBarHasData = settings.reaNumber || settings.fiscalCode || settings.bankName || settings.iban;
  const hasAnyInstitutional = studioHasData || agenziaHasData || reaBarHasData;
  // v1.8.0 · UX Operativa v2: dati istituzionali (recapiti + REA/IBAN)
  // sono collassati di default per liberare verticalmente ~150px e
  // permettere alla tabella operativa di occupare ≥80% dello schermo.
  // L'utente può espanderli con un click.
  const [showInstitutional, setShowInstitutional] = useState(false);

  return (
    <div className="mb-3 w-full">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-1">
        <div className="flex-1 flex items-center gap-3 flex-wrap">
          <h1
            className="gis-heading text-2xl font-bold tracking-tight leading-tight"
            style={{
              backgroundImage: "linear-gradient(90deg, var(--gis-green-dark), var(--gis-green))",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            Gestionale Gruppo Impresa
          </h1>
          {currentOperator && (
            <div className="flex items-center gap-1.5 text-xs gis-body">
              <UserCircle2 className="h-3.5 w-3.5" style={{ color: "var(--gis-green)" }} />
              <span className="text-slate-500">Op.:</span>
              <span className="font-semibold" style={{ color: "var(--gis-green)" }}>{currentOperator.fullName}</span>
            </div>
          )}
        </div>
        <div className="flex gap-1.5 flex-wrap items-center">
          <NotificationBell />
          <Button variant="outline" size="sm" onClick={loadAllData} disabled={isSaving} title="Aggiorna dati" className="h-8 px-2 hover:bg-slate-100 transition-colors">
            <RefreshCw className={`h-3.5 w-3.5 ${isSaving ? 'animate-spin' : ''}`} />
          </Button>
          {onLogout && (
            <Button variant="outline" size="sm" onClick={onLogout} className="h-8 px-2 text-slate-600 hover:text-red-600 hover:border-red-300 hover:bg-red-50 transition-colors" title="Esci dall'applicazione" data-testid="logout-button">
              <LogOut className="h-3.5 w-3.5 mr-1" />Esci
            </Button>
          )}
        </div>
      </div>

      {/* Toggle recapiti + dati istituzionali (collassabile · UX v2) */}
      {hasAnyInstitutional && (
        <button
          type="button"
          onClick={() => setShowInstitutional((v) => !v)}
          className="flex items-center gap-1 text-[10px] uppercase tracking-wider gis-heading text-slate-500 hover:text-[var(--gis-green-dark)] transition-colors mt-1"
          data-testid="toggle-institutional-bar"
        >
          {showInstitutional ? (
            <><ChevronUp className="h-3 w-3" /> Nascondi recapiti Studio · Agenzia · REA · IBAN</>
          ) : (
            <><ChevronDown className="h-3 w-3" /> Mostra recapiti Studio · Agenzia · REA · IBAN</>
          )}
        </button>
      )}

      {/* Dual company info: Studio + Agenzia */}
      {showInstitutional && (studioHasData || agenziaHasData) && (
        <div className="border-t border-slate-100 pt-2 grid grid-cols-1 md:grid-cols-2 gap-1" data-testid="company-info-bar">
          {studioHasData && (
            <div>
              <span className="text-[10px] font-semibold text-blue-600 uppercase flex items-center gap-1 mb-0.5">
                <Building2 className="h-3 w-3" />Studio
              </span>
              <InfoLine
                phone={settings.phoneStudio || settings.phone}
                email={settings.emailStudio || settings.email}
                pec={settings.pecStudio || settings.pec}
                sdi={settings.sdiCodeStudio}
              />
            </div>
          )}
          {agenziaHasData && (
            <div>
              <span className="text-[10px] font-semibold text-orange-600 uppercase flex items-center gap-1 mb-0.5">
                <Briefcase className="h-3 w-3" />Agenzia
              </span>
              <InfoLine
                phone={settings.phoneAgenzia}
                email={settings.emailAgenzia}
                pec={settings.pecAgenzia}
                sdi={settings.sdiCodeAgenzia}
              />
            </div>
          )}
        </div>
      )}

      {/* Identificativi istituzionali (REA + Codice Fiscale + Banca +
          IBAN dello Studio) — separati visivamente dai recapiti
          telefonici/email per coerenza con i documenti ufficiali.
          Aggiornato 22/02/2026: aggiunti Banca e IBAN sulla stessa
          riga orizzontale (richiesta utente), per esporre i dati
          bancari di pagamento direttamente nel cruscotto senza dover
          aprire Impostazioni. Tutti i badge usano flex-wrap, quindi
          su viewport stretti vanno a capo automaticamente. */}
      {showInstitutional && (settings.reaNumber || settings.fiscalCode || settings.bankName || settings.iban) && (
        <div
          className="mt-3 pt-2 border-t border-slate-100 flex flex-wrap items-center gap-x-5 gap-y-1"
          data-testid="company-rea-bar"
        >
          {settings.reaNumber && (
            <div className="flex items-center gap-2">
              <Landmark className="h-3.5 w-3.5 text-slate-500" />
              <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">REA</span>
              <span className="text-xs font-bold text-slate-700" data-testid="company-rea-value">
                {settings.reaNumber}
              </span>
            </div>
          )}
          {settings.fiscalCode && (
            <div className="flex items-center gap-2">
              <IdCard className="h-3.5 w-3.5 text-slate-500" />
              <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">C.F.</span>
              <span className="text-xs font-bold text-slate-700" data-testid="company-cf-value">
                {settings.fiscalCode}
              </span>
            </div>
          )}
          {settings.bankName && (
            <div className="flex items-center gap-2">
              <Banknote className="h-3.5 w-3.5 text-slate-500" />
              <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Banca</span>
              <span className="text-xs font-bold text-slate-700" data-testid="company-bank-value">
                {settings.bankName}
              </span>
            </div>
          )}
          {settings.iban && (
            <div className="flex items-center gap-2">
              <CreditCard className="h-3.5 w-3.5 text-slate-500" />
              <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">IBAN</span>
              <span
                className="text-xs font-bold text-slate-700 select-all font-mono tracking-tight"
                data-testid="company-iban-value"
                title="Doppio click per selezionare l'IBAN"
              >
                {settings.iban}
              </span>
              {/* Bottone copia rapida IBAN (richiesta ufficio 22/02/2026):
                  un click → IBAN negli appunti, toast di conferma. Usa
                  l'API `navigator.clipboard.writeText`, con fallback a
                  `document.execCommand('copy')` per i pochi browser
                  legacy in cui Clipboard API non è disponibile (es. uffici
                  con IE/Edge molto vecchi). */}
              <button
                type="button"
                onClick={async () => {
                  const value = settings.iban;
                  try {
                    if (navigator.clipboard && window.isSecureContext) {
                      await navigator.clipboard.writeText(value);
                    } else {
                      const ta = document.createElement("textarea");
                      ta.value = value;
                      ta.style.position = "fixed";
                      ta.style.opacity = "0";
                      document.body.appendChild(ta);
                      ta.select();
                      document.execCommand("copy");
                      document.body.removeChild(ta);
                    }
                    toast.success("IBAN copiato negli appunti");
                  } catch {
                    toast.error("Impossibile copiare l'IBAN");
                  }
                }}
                className="inline-flex items-center justify-center h-5 w-5 rounded text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                title="Copia IBAN"
                aria-label="Copia IBAN"
                data-testid="company-iban-copy-button"
              >
                <Copy className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function OperatorWarningBanner({ operators, setIsOperatorLinkDialogOpen }) {
  return (
    <div
      className="mb-4 p-4 rounded-lg flex items-center justify-between"
      style={{
        backgroundColor: "var(--gis-gold-50)",
        border: "1px solid var(--gis-gold)",
      }}
    >
      <div className="flex items-center gap-3">
        <AlertTriangle className="h-5 w-5" style={{ color: "var(--gis-gold-dark)" }} />
        <div>
          <p className="gis-heading font-semibold" style={{ color: "var(--gis-gold-dark)" }}>
            Identità operatore non configurata
          </p>
          <p className="text-sm gis-body" style={{ color: "var(--gis-gold-dark)" }}>
            Collega il tuo account a un profilo operatore per tracciare correttamente le attività.
          </p>
        </div>
      </div>
      <Button
        onClick={() => setIsOperatorLinkDialogOpen(true)}
        className="gis-btn-primary text-white"
        style={{
          backgroundColor: "var(--gis-gold-dark)",
        }}
        data-testid="link-operator-button"
      >
        <UserCircle2 className="mr-2 h-4 w-4" />
        Collega Operatore
      </Button>
    </div>
  );
}

export function NoOperatorsInfoBanner() {
  return (
    <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
      <AlertTriangle className="h-5 w-5 text-blue-600" />
      <div>
        <p className="font-medium text-blue-800">Nessun operatore registrato</p>
        <p className="text-sm text-blue-600">
          Crea un operatore nella sezione "Operatori" e poi collegalo al tuo account.
        </p>
      </div>
    </div>
  );
}

